import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/components/ProductCard';
import { useCart } from '@/lib/cart-context';

export default function PaymentResult() {
  const { clear } = useCart();
  const [state, setState] = useState({ loading: true });
  const orderId = new URLSearchParams(window.location.search).get('order_id') || '';

  useEffect(() => {
    if (!orderId) {
      setState({ loading: false, status: 'unknown' });
      return;
    }
    const token = sessionStorage.getItem(`zc_order_${orderId}`) || '';

    base44.functions
      .invoke('jazzcashStatus', { order_id: orderId, access_token: token })
      .then((res) => {
        const data = res.data || {};
        setState({ loading: false, ...data });
        if (data.status === 'paid') {
          // Complete the same post-payment steps the checkout runs for other methods.
          base44.functions
            .invoke('generateOrderNumber', { order_id: orderId, access_token: token, phone: data.phone })
            .then((r) => {
              const number = r?.data?.order_number;
              if (number) setState((s) => ({ ...s, order_number: number }));
            })
            .catch(() => {});
          base44.functions.invoke('createFlowOrder', { order_id: orderId, access_token: token }).catch(() => {});
          sessionStorage.removeItem(`zc_order_${orderId}`);
          // The cart stayed intact in case the payment failed; the order is paid now.
          clear();
        }
      })
      .catch(() => setState({ loading: false, status: 'unknown' }));
  }, [orderId]);

  if (state.loading) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-6 text-sm text-muted-foreground">Confirming your payment…</p>
      </div>
    );
  }

  const paid = state.status === 'paid';

  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${paid ? 'bg-primary/10' : 'bg-destructive/10'}`}>
        {paid ? <Check className="h-8 w-8 text-primary" /> : <X className="h-8 w-8 text-destructive" />}
      </div>

      <h1 className="mt-6 font-display text-3xl tracking-tight">
        {paid ? 'Payment received' : 'Payment not completed'}
      </h1>

      {state.order_number && (
        <p className="mt-2 text-sm text-muted-foreground">
          Order <span className="font-mono">{state.order_number}</span>
          {typeof state.total === 'number' ? ` · ${formatPrice(state.total)}` : ''}
        </p>
      )}

      <p className="mt-3 text-muted-foreground">
        {paid
          ? 'Your JazzCash payment is confirmed and your order is being prepared for shipping.'
          : state.status === 'pending'
            ? 'JazzCash has not confirmed this payment yet. If money left your account, it will be reconciled shortly — we will email you once it is confirmed.'
            : 'This payment was cancelled or declined, so your order is still unpaid. You can place it again with any payment method.'}
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <Button asChild className="focus-ring"><Link to="/shop">Continue shopping</Link></Button>
        {paid ? (
          <Button asChild variant="outline" className="focus-ring"><Link to="/account">View my orders</Link></Button>
        ) : (
          <Button asChild variant="outline" className="focus-ring"><Link to="/checkout">Back to checkout</Link></Button>
        )}
      </div>
    </div>
  );
}